import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import AuthShell, { googleLogin } from "../components/AuthShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

function GoogleButton({ label }) {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-btn"
      className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-6 py-3 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--ink)]">
      <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      {label}
    </button>
  );
}

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/app", { replace: true }); }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    try {
      await register(name, email, password);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Create account" description="Create your StUpEco Hub account." path="/signup" />
      <AuthShell
        title="Create your account"
        subtitle="Join the StUpEco Hub member area."
        footer={<>Already have an account? <Link to="/login" className="font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline">Log in</Link></>}
      >
        <GoogleButton label="Sign up with Google" />
        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-[color:var(--hairline)]" />
          <span className="text-xs uppercase tracking-wider text-[color:var(--ink-soft)]">or</span>
          <span className="h-px flex-1 bg-[color:var(--hairline)]" />
        </div>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form" noValidate>
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="signup-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="signup-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="h-11 rounded-xl" />
          </div>
          <button type="submit" disabled={submitting} data-testid="signup-submit" className="group inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : <>Create account <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
          </button>
        </form>
      </AuthShell>
    </>
  );
}
